/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { AppBar, Box, Toolbar, useMediaQuery } from '@material-ui/core';
import { useLocation } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import MenuIcon from '@material-ui/icons/Menu';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import Tooltip from 'components/common/Tooltip';
import Logo from '../Logo/Logo';
import TopNavigation from '../menu/TopNavigation';
import Sidebar from '../sidebar/Sidebar';
import { SidebarContext } from '../sidebar/useSidebar';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { isFeatureEnabled, ToggleableFeature } from '../../shared/utils/featuresUtils';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ProcedureCategory } from '../../procedure/SubmissionProcedure';
import { RoutePath } from '../../shared/enums/RoutePath';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  root: {
    backgroundColor: theme.palette.white,
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    color: theme.palette.mainText,
    paddingLeft: '24px',
    paddingRight: '24px',
    zIndex: ({ isNewSubmissionPage }) => !isNewSubmissionPage && theme.zIndex.drawer + 100,
    alignContent: 'center',
    boxShadow: ({ showConfigurableHeader }) =>
      !showConfigurableHeader ? '0 0.125rem 0.625rem rgb(0 0 0 / 10%)' : 'unset',
    [theme.breakpoints.down('sm')]: {
      height: '3.75rem',
      paddingTop: '0',
      paddingBottom: '0',
      '& .MuiToolbar-gutters': {
        paddingLeft: '0.5rem',
      },
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  menuButton: {
    backgroundColor: theme.palette.white,
    marginLeft: '0rem',
    [theme.breakpoints.down('sm')]: {
      height: '3rem',
    },
  },
  strong: {
    fontWeight: 'bold',
  },
  drawerButton: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '1.4rem',
    [theme.breakpoints.down('sm')]: {
      marginRight: '0',
      marginLeft: '0.5rem',
    },
  },
  menuIcon: {
    backgroundColor: theme.palette.iconBackground,
  },
  topNav: {
    justifyContent: 'flex-end',
  },
  title: {
    flexGrow: 1,
    textTransform: 'uppercase',
    fontSize: theme?.typography?.title?.fontSize || 'small',
    alignSelf: 'center',
    padding: '1rem',
    fontWeight: 700,
    lineHeight: '1.5rem',
  },
  flexBox: {
    display: 'flex',
    justifyContent: ({ showConfigurableHeader }) => (showConfigurableHeader ? 'unset' : 'center'),
    paddingLeft: ({ showConfigurableHeader }) => (showConfigurableHeader ? '50%' : 'unset'),
  },
  noPadding: {
    padding: 0,
  },
  menuButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginRight: ({ showConfigurableHeader }) => showConfigurableHeader && 'auto',
  },
}));

export default function Header({ breakpointSmall, user, currentOrganization, showConfigurableHeader }) {
  const { t } = useTranslation();
  const location = useLocation();
  const noMenuPathNames = ['/draft', '/errorpermission', '/signing'];
  const isNoMenuPage = noMenuPathNames.some(noMenuPathname => location.pathname.includes(noMenuPathname));
  const isUmaRoute = RoutePath.isUmaRoute(location.pathname);
  // Configurable header animation
  const [stickyHeader, setStickyHeader] = useState(false);
  const headerRef = useRef(null);

  const applyStickyHeader = () => {
    const topOffset = headerRef.current?.getBoundingClientRect().top;

    if (window.pageYOffset > topOffset) {
      setStickyHeader(true);
    } else {
      setStickyHeader(false);
    }
  };

  useEffect(() => {
    if (showConfigurableHeader) {
      window.addEventListener('scroll', applyStickyHeader);
    }
    return () => {
      if (showConfigurableHeader) {
        window.removeEventListener('scroll', applyStickyHeader);
      }
    };
  }, [headerRef.current, isNoMenuPage, isUmaRoute]);

  let mainHeaderPosition = !isNoMenuPage && !isUmaRoute && !showConfigurableHeader ? 'fixed' : 'relative';
  if (stickyHeader) {
    mainHeaderPosition = 'sticky';
  }

  const classes = useStyles({ isNewSubmissionPage: isNoMenuPage || isUmaRoute, showConfigurableHeader, stickyHeader });
  const { onMenuButtonClick, isExpandedDrawer } = useContext(SidebarContext);
  const isMediumOrSmaller = useMediaQuery(theme => theme.breakpoints.down('md'));
  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);
  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);

  const procedures = globalConfiguration.resource?.procedures;
  const sidebarConfig = _.cloneDeep(frontendCustomisation.resource?.sidebar);
  if (sidebarConfig && procedures) {
    const proceduresSubmenuConfig = sidebarConfig.find(item => item.procedures);
    // TODO: I would really like to know why this configuration is overwritten here
    // TODO: I agree with the above TODO comment. I think we should get rid of the sidebarConfig
    // at least for procedures since they should always be aligned with the procedures in
    // config.yml
    proceduresSubmenuConfig.subList = Object.values(procedures)
      .filter(procedure => procedure.category !== ProcedureCategory.SUBSEQUENT_ACTIONS && procedure.enabled)
      .map(procedure => ({
        text: procedure.label,
        link: RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
          procedure: procedure.id,
        }),
      }));
  }

  const isMenuVisible = () => {
    return (!isNoMenuPage || showConfigurableHeader) && !isMediumOrSmaller;
  };

  const filterSideBarList = (sideBarList) => {
    if (sideBarList && !isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.ADDRESS_BOOK)) {
      return sideBarList.filter(element => element.link !== RoutePath.ADDRESSBOOK_SEARCH.path);
    }

    return sideBarList;
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <>
      {showConfigurableHeader && (
        <AppBar
          data-testid="configrable-header"
          position="relative"
          className={`${classes.root} ${classes.noPadding}`}
          elevation={0}
        >
          <div className={classes.flexBox}>
            <Logo
              breakpointSmall={breakpointSmall}
              isNoMenuPage={isNoMenuPage}
              showConfigurableHeader={showConfigurableHeader}
            />
          </div>
        </AppBar>
      )}
      <AppBar ref={headerRef} position={mainHeaderPosition} className={classes.root}>
        <Toolbar className={classes.toolbar}>
          <div className={classes.menuButtonContainer}>
            {!isNoMenuPage && !isUmaRoute && frontendCustomisation.ready && (
              <Box onClick={onMenuButtonClick} className={classes.drawerButton}>
                <Tooltip
                  className="body-row-tooltip"
                  title={t('Menu')}
                  placement="right"
                  arrow
                  enterDelay={500}
                  enterNextDelay={500}
                  open={tooltipOpen}
                  onOpen={() => setTooltipOpen(true)}
                  onClose={() => setTooltipOpen(false)}
                >
                  <IconButton
                    color="inherit"
                    className={classes.menuIcon}
                    aria-label={t('aria.header.open.sidebar')}
                    onMouseLeave={() => setTooltipOpen(false)}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {!showConfigurableHeader && <Logo breakpointSmall={breakpointSmall} isNoMenuPage={isNoMenuPage} />}
          </div>

          {isMenuVisible() && (
            <Box display="flex">
              <TopNavigation user={user} currentOrganization={currentOrganization} />
            </Box>
          )}
          {isNoMenuPage && isMediumOrSmaller && (
            <Box display="flex" className={classes.topNav}>
              <TopNavigation user={user} currentOrganization={currentOrganization} />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {!isNoMenuPage && !showConfigurableHeader && <Toolbar />}
      {!isNoMenuPage && !isUmaRoute && frontendCustomisation.ready && (
        <Sidebar
          showConfigurableHeader={showConfigurableHeader}
          sidebarList={filterSideBarList(sidebarConfig)}
          globalConfiguration={globalConfiguration}
          open={isExpandedDrawer}
          user={user}
          currentOrganization={currentOrganization}
        />
      )}
    </>
  );
}
